import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { qrImageUrl } from '../hooks/usePersonalQrCode';
import {
  computePersonalQrPayload, computePersonalManualCode,
  computeUserPassPayload, computeUserPassManualCode,
  STEP_MS, stepFor,
} from '../utils/qrRotation';

/**
 * RotatingAccessQr (Phase 2, 2026-09-12): shared rotating QR pass renderer for
 * codes that carry a per-code qr_secret (resident/staff personal codes AND
 * guest codes from my-codes, plus the post-claim pass on the public invite
 * landing). Renders the signed VQR1 payload QR (60s rotation, +/-1 step grace)
 * + the 8 manual digits a guard can type + a countdown bar.
 *
 * Extracted from the inline personal-QR rendering in MyQR.tsx so guest codes
 * and the invite landing reuse the exact same rotation math (computePersonalQr
 * Payload/computePersonalManualCode from utils/qrRotation.ts — byte-identical
 * with the backend validator). When qrSecret is missing, render nothing — the
 * caller falls back to the static-code QR (older backend).
 */
export function RotatingAccessQr({ code, qrSecret, size = 240, variant = 'vqr1', userId, tone = 'auto' }: {
  code: string;
  qrSecret: string;
  size?: number;
  /** ADDENDUM 2026-09-14b: 'vqr1' (default, per-code) or 'vqr2' (user-identity
      pass). In 'vqr2' mode pass userId + the per-USER qr_secret from
      /qr-access/my-pass; payload = VQR2:<userId>.<step>.<sig>. */
  variant?: 'vqr1' | 'vqr2';
  /** Subject user id — required in 'vqr2' mode. */
  userId?: string;
  /** 'light' (2026-09-15 live-QA fix): caller renders on an ALWAYS-white card
      even in dark mode (public invite landing) — pin the manual code to a
      dark-on-white color instead of the theme-aware dark: variant (light teal
      on white was unreadable). Default 'auto' keeps theme-aware colors. */
  tone?: 'auto' | 'light';
}) {
  const isVqr2 = variant === 'vqr2';
  const subjectId = isVqr2 ? userId : code;
  const [payload, setPayload] = useState<string | null>(null);
  const [manual, setManual] = useState<string | null>(null);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  useEffect(() => {
    if (!subjectId || !qrSecret) {
      setPayload(null);
      setManual(null);
      setRemainingMs(null);
      return;
    }
    let alive = true;
    let renderedStep = -1;
    const recompute = (nowMs: number) => {
      Promise.all([
        isVqr2
          ? computeUserPassPayload(subjectId!, qrSecret, nowMs)
          : computePersonalQrPayload(code, qrSecret, nowMs),
        isVqr2
          ? computeUserPassManualCode(subjectId!, qrSecret, nowMs)
          : computePersonalManualCode(code, qrSecret, nowMs),
      ])
        .then(([p, m]) => {
          if (!alive) return;
          setPayload(p);
          setManual(m);
          renderedStep = stepFor(nowMs);
        })
        .catch(() => {
          if (alive) { setPayload(null); setManual(null); }
        });
    };
    const tick = () => {
      const now = Date.now();
      setRemainingMs(STEP_MS - (now % STEP_MS));
      const wallStep = stepFor(now);
      if (wallStep !== renderedStep) recompute(now);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId, qrSecret, isVqr2]);

  if (!subjectId || !qrSecret) return null;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="p-3 bg-white border-2 border-slate-200 rounded-lg">
        {payload ? (
          <img
            src={qrImageUrl(payload, size)}
            alt="QR de acceso (rota cada minuto)"
            style={{ width: size, height: size }}
          />
        ) : (
          <div style={{ width: size, height: size }} className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        )}
      </div>
      {manual && (
        <>
          <p
            className={[
              'font-mono text-2xl tracking-widest',
              tone === 'light'
                ? 'text-slate-900'
                : 'text-[var(--v-teal-700)] dark:text-[var(--v-teal-400)]',
            ].join(' ')}
          >
            {manual}
          </p>
          {remainingMs !== null && (
            <div className="w-40" title={`El código cambia en ${Math.ceil(remainingMs / 1000)}s`}>
              <div className="h-1 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[var(--v-teal-500)] dark:bg-[var(--v-teal-400)]"
                  style={{ width: `${(remainingMs / STEP_MS) * 100}%`, transition: 'width 1s linear' }}
                />
              </div>
              <p
                className={[
                  'text-[11px] mt-1 text-center',
                  tone === 'light' ? 'text-slate-500' : 'text-slate-400 dark:text-slate-500',
                ].join(' ')}
              >
                El código cambia en {Math.ceil(remainingMs / 1000)}s — muestra el QR actual en la caseta
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default RotatingAccessQr;
